/**
 * React Query hooks for Character CRUD operations.
 * Uses tRPC client for type-safe API calls.
 *
 * Characters are auto-created by the backend with role="pending" when
 * Fountain character names are detected in page saves. The list hook
 * supports polling to pick up these new characters in near-real-time.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import type { Character } from "../types";

// List characters, optionally filtered by serialId or groupId
// Supports polling: set `pollIntervalMs` to enable periodic refetching
// (useful for picking up auto-detected "pending" characters)
export function useCharacters(
  filters?: { serialId?: string; groupId?: string },
  options?: { pollIntervalMs?: number }
) {
  return useQuery({
    queryKey: ["characters", "list", filters],
    queryFn: () => trpc.character.list.query(filters || {}),
    refetchInterval: options?.pollIntervalMs ?? false,
  }) as { data: Character[]; isLoading: boolean; isError: boolean; error: any };
}

// Get a single character by ID
export function useCharacter(characterId: string) {
  return useQuery({
    queryKey: ["characters", "get", characterId],
    queryFn: () => trpc.character.get.query(characterId),
    enabled: !!characterId,
  }) as { data: Character | undefined; isLoading: boolean; isError: boolean; error: any };
}

// Create a new character
export function useCreateCharacter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      key: string;
      title: string;
      description?: string;
      serialId: string;
      body: string;
      groupId?: string;
      role?: string;
    }) => {
      return await trpc.character.create.mutate(input as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["characters"] });
    },
  });
}

// Update an existing character
export function useUpdateCharacter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string } & Partial<Character>) => {
      return await trpc.character.update.mutate(input as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["characters"] });
    },
  });
}

// Delete a character
export function useDeleteCharacter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return await trpc.character.delete.mutate(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["characters"] });
    },
  });
}